import React, { useState } from "react";
import {
  View,
  TouchableOpacity,
  Text,
  StyleSheet,
  Platform,
  ActivityIndicator,
  Dimensions,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useSelector, useDispatch } from "react-redux";
import { createApiClient, apiClient } from "../../api/apiClient";
import { useToast } from "../../components/LhToastContainer";
import FontFamily from "../../constants/fonts";
import { setSuccessMessage } from "../../store/slices/sparcWalkSlice";
import { useTheme } from "../../theme";
interface RootState {
  auth: {
    user?: {
      username?: string;
    };
  };
  sparcWalk: {
    selectedWalk?: {
      id: string | number;
      walkData?: any;
    };
  };
}

interface DropdownItem {
  id: string;
  displayText: string;
}

interface SparcWalkData {
  location: string;
  walkType: string;
  period: number;
  week: number;
  zone: string;
  conversedWith: string;
  managerSignature: string;
  miscellImage: string;
  miscellImage2: string;
  miscellImageDesc: string;
  miscellImage2Desc: string;
  updatedImg1: boolean;
  updatedImg2: boolean;
  updatedManagerSign: boolean;
  notes: string;
  sparcSolutions: string;
  sparcCategorySelected: string;
  sparcExpectedAnswers: string;
  created: string;
  createdBy: string;
  modified: string;
  modifiedBy: string;
  status: string;
}

interface SparcWalkImage {
  ImageId: string;
  ImageBase64: string;
  Description: string;
  ObservationId: number;
}

interface Props {
  selectedEmployee: { id: string; displayText: string } | null;
  selectedZone: DropdownItem;
  selectedCategory: DropdownItem;
  showStatusMessage: (type: "success" | "error", message: string) => void;
  onSubmit: (
    action: "save" | "complete",
    data: {
      oneSubmission: boolean;
      selectedRecord: { id: number };
      walkData: SparcWalkData;
      toolsMapping: Array<{ toolId: number }>;
      questionsMapping: Array<{ toolId: number }>;
      sparcWalkImages: SparcWalkImage[];
    }
  ) => Promise<void>;
  walkState: "new" | "in-progress" | "completed";
  isDisabled?: boolean;
  selectedBehaviorIds: string[];
  selectedQuestionIds: string[];
  observations: string;
  possibleSolutions: string;
  sparcExpectedAnswers: string;
  walkData: SparcWalkData;
  sparcWalkImages: SparcWalkImage[];
}

// Helper to detect iPad
const isIPad = Platform.OS === 'ios' && Dimensions.get('window').width >= 768;

const SparcWalkInspectionAction: React.FC<Props> = ({
  selectedEmployee,
  selectedZone,
  selectedCategory,
  onSubmit,
  walkState,
  isDisabled = false,
  walkData,
  sparcWalkImages,
  selectedBehaviorIds,
  selectedQuestionIds,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const dispatch = useDispatch();
  const toast = useToast();

  // Get data from Redux state
  const userEmail = useSelector(
    (state: RootState) => state.auth.user?.username
  );
  const selectedWalk = useSelector(
    (state: RootState) => state.sparcWalk.selectedWalk
  );

  const handleSaveForLater = async () => {
    try {
      setIsSubmitting(true);
      await onSubmit("save", {
        oneSubmission: true,
        selectedRecord: { id: 0 },
        walkData: {
          ...walkData,
          status: "in-progress",
        },
        toolsMapping: selectedBehaviorIds.map((id) => ({ toolId: Number(id) })),
        questionsMapping: selectedQuestionIds.map((id) => ({
          toolId: Number(id),
        })),
        sparcWalkImages,
      });
      
      // Set success message in Redux
      dispatch(setSuccessMessage("SPARC Walk saved successfully."));
    } catch (error: any) {
      console.error("Save Error:", error);
      toast.showToast({
        header: "Error",
        subtext: error?.message || "Failed to save SPARC Walk",
        variant: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleComplete = async () => {
    try {
      setIsSubmitting(true);
      await onSubmit("complete", {
        oneSubmission: true,
        selectedRecord: { id: 0 },
        walkData: {
          ...walkData,
          status: "completed",
        },
        toolsMapping: selectedBehaviorIds.map((id) => ({ toolId: Number(id) })),
        questionsMapping: selectedQuestionIds.map((id) => ({
          toolId: Number(id),
        })),
        sparcWalkImages,
      });
      
      // Set success message in Redux
      dispatch(setSuccessMessage("SPARC Walk completed successfully."));
    } catch (error: any) {
      console.error("Complete Error:", error);
      toast.showToast({
        header: "Error",
        subtext: error?.message || "Failed to complete SPARC Walk",
        variant: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePdfExport = async () => {
    if (!selectedWalk?.id || !userEmail) {
      toast.showToast({
        header: "PDF Export Error",
        subtext: "Walk must be saved before exporting PDF.",
        variant: "error",
      });
      return;
    }

    try {
      setIsPdfExporting(true);

      // Build the subject line with meaningful context
      const recordName = selectedEmployee?.displayText || "Unknown";
      const subjectLine = `SPARC Walk Report - ${selectedCategory.displayText} - ${selectedZone.displayText} - ${recordName}`;

      const api = createApiClient();
      const response = await apiClient(
        api,
        `/notifications/email/pdf?emailTo=${encodeURIComponent(
          userEmail
        )}&inspection_module=sparc_walk&id=${
          selectedWalk.id
        }&subject=${encodeURIComponent(subjectLine)}`,
        "GET"
      );

      if (response && response.message) {
        toast.showToast({
          header: "Email Notification",
          subtext: response.message,
          variant: response.messageId ? "success" : "error",
        });
      } else {
        toast.showToast({
          header: "Email Error",
          subtext: "Failed to send PDF export email",
          variant: "error",
        });
      }
    } catch (error: any) {
      console.error("PDF Export Error:", error);
      toast.showToast({
        header: "Email Error",
        subtext: error?.message || "Failed to export PDF",
        variant: "error",
      });
    } finally {
      setIsPdfExporting(false);
    }
  };

  const isPdfExportDisabled =
    isDisabled ||
    !selectedWalk?.id ||
    walkState === "new" ||
    walkState === "in-progress";
  const isCompletedWalk = walkState === "completed";

  return (
    <View style={styles.actionRow}>
      {/* PDF Export Button - separate on mobile, part of row on iPad/web */}
      <TouchableOpacity
        style={[
          isPdfExportDisabled
            ? styles.disabledButton
            : styles.pdfExportButton,
          styles.pdfButtonContainer,
          isDark &&
            !isPdfExportDisabled && {
              backgroundColor: colors.brand.primary,
              borderColor: colors.brand.primary,
            },
          isDark &&
            isPdfExportDisabled && {
              backgroundColor: colors.background.elevated,
              borderColor: colors.border.default,
            },
          Platform.OS === "web" && !isPdfExportDisabled
            ? { cursor: "pointer" as any }
            : {},
        ]}
        onPress={isPdfExportDisabled ? () => {} : handlePdfExport}
        disabled={isPdfExportDisabled}
        accessibilityState={{ disabled: isPdfExportDisabled }}
        accessibilityLabel={
          isPdfExportDisabled
            ? "PDF Export Not Available"
            : isPdfExporting
            ? "Sending PDF export email"
            : "PDF Export"
        }
      >
        <View style={styles.buttonContent}>
          {isPdfExportDisabled ? (
            <>
              <MaterialIcons
                name="picture-as-pdf"
                size={24}
                color={isDark ? colors.text.secondary : "#B1B1B1"}
                style={{marginRight: 8}}
              />
              <Text
                style={[styles.buttonTextDisabled, isDark && { color: colors.text.secondary }]}
              >
                PDF Export
              </Text>
            </>
          ) : isPdfExporting ? (
            <>
              <ActivityIndicator
                size="small"
                color="#FFF"
                style={{marginRight: 8}}
              />
              <Text style={[styles.buttonText, isDark && { color: colors.text.inverse }]}>
                Sending Email...
              </Text>
            </>
          ) : (
            <>
              <MaterialIcons
                name="picture-as-pdf"
                size={24}
                color={colors.text.inverse}
                style={{marginRight: 8}}
              />
              <Text style={[styles.buttonText, isDark && { color: colors.text.inverse }]}>
                PDF Export
              </Text>
            </>
          )}
        </View>
      </TouchableOpacity>
      
      {/* Helper text for disabled PDF export removed per request */}

      {/* Save for Later Button */}
      <TouchableOpacity
        style={[
          styles.customButton,
          styles.saveButtonContainer,
          isSubmitting || isDisabled || isCompletedWalk
            ? styles.disabledButton
            : styles.saveButton,
          Platform.OS === "web" &&
          !(isSubmitting || isDisabled || isCompletedWalk)
            ? { cursor: "pointer" as any }
            : {},
          isDark &&
            !(isSubmitting || isDisabled || isCompletedWalk) && {
              backgroundColor: colors.background.surface,
              borderColor: colors.brand.primary,
            },
          isDark &&
            (isSubmitting || isDisabled || isCompletedWalk) && {
              backgroundColor: colors.background.elevated,
              borderColor: colors.border.default,
            },
        ]}
        onPress={handleSaveForLater}
        disabled={isSubmitting || isDisabled || isCompletedWalk}
        accessibilityState={{
          disabled: isSubmitting || isDisabled || isCompletedWalk,
        }}
        accessibilityLabel={
          isSubmitting
            ? "Save for later (disabled during submission)"
            : isCompletedWalk
            ? "Save for later (disabled for completed walk)"
            : "Save for later"
        }
      >
        <View style={styles.buttonContent}>
          {isSubmitting ? (
            <ActivityIndicator
              size="small"
              color={isDark ? colors.brand.primary : "#034895"}
              style={{marginRight: 8}}
            />
          ) : (
            <>
              <MaterialIcons
                name="save"
                size={24}
                color={
                  isSubmitting || isDisabled || isCompletedWalk
                    ? isDark
                      ? colors.text.secondary
                      : "#B1B1B1"
                    : isDark
                    ? colors.brand.primary
                    : "#034895"
                }
                style={{marginRight: 8}}
              />
              <Text
                style={
                  isSubmitting || isDisabled || isCompletedWalk
                    ? [styles.buttonTextDisabled, isDark && { color: colors.text.secondary }]
                    : [styles.saveButtonText, isDark && { color: colors.brand.primary }]
                }
              >
                Save for Later
              </Text>
            </>
          )}
        </View>
      </TouchableOpacity>

      {/* Complete Button */}
      <TouchableOpacity
        style={[
          styles.customButton,
          styles.completeButtonContainer,
          isSubmitting || isDisabled || isCompletedWalk
            ? styles.disabledButton
            : styles.completeButton,
          Platform.OS === "web" &&
          !(isSubmitting || isDisabled || isCompletedWalk)
            ? { cursor: "pointer" as any }
            : {},
          isDark &&
            !(isSubmitting || isDisabled || isCompletedWalk) && {
              backgroundColor: colors.brand.primary,
              borderColor: colors.brand.primary,
            },
          isDark &&
            (isSubmitting || isDisabled || isCompletedWalk) && {
              backgroundColor: colors.background.elevated,
              borderColor: colors.border.default,
            },
        ]}
        onPress={handleComplete}
        disabled={isSubmitting || isDisabled || isCompletedWalk}
        accessibilityState={{
          disabled: isSubmitting || isDisabled || isCompletedWalk,
        }}
        accessibilityLabel={
          isSubmitting
            ? "Complete form (disabled during submission)"
            : isCompletedWalk
            ? "Complete form (disabled for completed walk)"
            : "Complete form"
        }
      >
        <View style={styles.buttonContent}>
          {isSubmitting ? (
            <ActivityIndicator
              size="small"
              color={colors.text.inverse}
              style={{marginRight: 8}}
            />
            ) : (
              <Text
                style={
                  isSubmitting || isDisabled || isCompletedWalk
                    ? [styles.buttonTextDisabled, isDark && { color: colors.text.secondary }]
                    : [styles.buttonText, isDark && { color: colors.text.inverse }]
                }
              >
                Complete
              </Text>
            )}
        </View>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  actionRow: {
    flexDirection: Platform.OS === "web" || isIPad ? "row" : "column",
    flexWrap: "wrap",
    marginTop: 24,
    //paddingTop: 24,
    paddingBottom: 16,
    alignItems: Platform.OS === "web" || isIPad ? "flex-start" : "stretch",
    justifyContent: Platform.OS === 'web' || isIPad ? 'flex-start' : 'center',
    width: "100%",
    gap: Platform.OS === "web" || isIPad ? 20 : 8,
  },
  errorContainer: {
    backgroundColor: "#ffdddd",
    padding: 12,
    borderRadius: 6,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#ff8888",
  },
  errorText: {
    color: "#d32f2f",
    fontSize: 14,
    textAlign: "center",
    fontFamily: FontFamily.regular,
  },
  componentWrapper: {
    flex: Platform.OS === "web" ? 0 : undefined,
    marginRight: Platform.OS === "web" ? 20 : 0,
    minWidth: Platform.OS === "web" ? 189 : "100%",
    marginBottom: Platform.OS === "web" ? 0 : 10,
  },
  buttonColumn: {
    flex: Platform.OS === "web" ? 0 : undefined,
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 16,
    minWidth: Platform.OS === "web" ? 394 : "100%",
    width: Platform.OS === "web" ? "auto" : "100%",
  },
  buttonFlex: {
    flex: 1,
    minWidth: 0,
  },
  customButton: {
    paddingVertical: 8,
    paddingHorizontal: 24,
    borderRadius: 24,
    backgroundColor: "#034895",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1 as number,
    shadowRadius: 3,
    elevation: 2,
    minWidth: 120,
    height: 40,
    alignItems: "center",
    justifyContent: "center",
  },
  saveButton: {
    backgroundColor: "#FFFFFF",
    borderRadius: 24,
    borderWidth: 1,
    borderColor: "#034895",
    paddingVertical: 8,
    paddingHorizontal: 24,
    height: 40,
    width: Platform.OS === "web" || isIPad ? 189 : "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  completeButton: {
    backgroundColor: "#034895",
    borderRadius: 24,
    paddingVertical: 8,
    paddingHorizontal: 24,
    height: 40,
    width: Platform.OS === "web" || isIPad ? 189 : "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  pdfExportButton: {
    backgroundColor: "#034895",
    borderRadius: 24,
    paddingVertical: 8,
    paddingHorizontal: 24,
    height: 40,
    width: Platform.OS === "web" || isIPad ? 189 : "100%",
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1 as number,
    shadowRadius: 3,
    elevation: 2,
  },
  disabledButton: {
    opacity: 1,
    backgroundColor: "#F5F5F5",
    borderWidth: 1,
    borderColor: "#DADADA",
    borderRadius: 24,
    paddingVertical: 8,
    paddingHorizontal: 24,
    height: 40,
    width: Platform.OS === "web" || isIPad ? 189 : "100%",
    shadowOpacity: 0.1 as number,
    alignItems: "center",
    justifyContent: "center",
    ...(Platform.OS === "web" ? { cursor: "not-allowed" as any } : {}),
  },
  buttonContent: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    gap: 8,
  },
  buttonText: {
    color: "white",
    fontSize: 14,
    fontWeight: "700",
    fontFamily: FontFamily.regular,
    textAlign: "center",
    lineHeight: 22,
    letterSpacing: 0,
  },
  saveButtonText: {
    color: "#034895",
    fontSize: 14,
    fontWeight: "700",
    fontFamily: FontFamily.regular,
    fontStyle: "normal", 
    textAlign: "center",
    lineHeight: 22,
    letterSpacing: 0,
  },
  buttonTextDisabled: {
    color: "#B1B1B1",
    fontSize: 14,
    fontWeight: "700",
    fontFamily: FontFamily.regular,
    lineHeight: 22,
    letterSpacing: 0,
  },
  pdfButtonContainer: {
    width: Platform.OS === "web" || isIPad ? 189 : "100%",
    marginBottom: Platform.OS === "web" || isIPad ? 0 : 10,
  },
  saveButtonContainer: {
    width: Platform.OS === "web" || isIPad ? 189 : "100%",
    marginBottom: Platform.OS === "web" || isIPad ? 0 : 10,
  },
  completeButtonContainer: {
    width: Platform.OS === "web" || isIPad ? 189 : "100%",
    marginBottom: Platform.OS === "web" || isIPad ? 0 : 10,
  },
  helperText: {
    color: "#616161",
    fontSize: 12,
    fontFamily: FontFamily.regular,
    fontWeight: "400",
    fontStyle: "normal",
    lineHeight: 16,
    letterSpacing: 0,
    marginTop: 8,
    textAlign: "center",
    maxWidth: Platform.OS === "web" || isIPad ? 189 : "100%",
  },
  readOnlyMessage: {
    backgroundColor: "#f8d7da",
    borderColor: "#f5c6cb",
    borderWidth: 1,
    borderRadius: 4,
    padding: 12,
    marginBottom: 15,
  },
  readOnlyText: {
    color: "#721c24",
    fontSize: 14,
    textAlign: "center",
    fontFamily: FontFamily.regular,
  },
});

export default SparcWalkInspectionAction;
